/* ============================================================
   RUME Welcome Funnel — JS (2026-05-10, V2 redesign)
   Side-panel desktop / bottom-sheet mobile. Composes with rume-welcome-funnel.liquid + rume-welcome.css.

   V2 changes (2026-05-10):
   - Drag-to-dismiss on mobile sheet (touch events, threshold ~120px)
   - Refresh-on-home reset rule: home page on draft theme clears state
     when reached via reload OR fresh navigation (empty/external referrer).
     Internal click home → PDP → home does NOT reset.
   - Slower ticker animation (1500ms) + delayed start to feel less hurried.

   State machine:
     - rume_state: 'lead' | 'declined' | (absent)
     - rume_state_ts: ISO timestamp
     - rume_phone + rume_preference

   Triggers (when state absent):
     - 1.5s (mobile) / 2.8s (desktop) after DOMContentLoaded OR scroll past 30% — first to fire

   Retry mode (declined users):
     - openPopup({ mode: 'retry' }) swaps copy + skips ticker count-up
     - Corner button opens in retry mode (only visible in declined state)
   ============================================================ */

(function () {
  'use strict';

  const root = document.querySelector('[data-rume-welcome-root]');
  if (!root) return;

  // The welcome funnel now runs on the PDP/home pages too (its popup is no longer
  // hidden there — see the un-hide in rume-home.css.liquid + rume-order-page.liquid),
  // so we no longer bail on [data-rume-pdp]. The popup can paint and close normally.

  const PREVIEW_MODE = root.dataset.previewMode === 'true';
  const DISCOUNT_PCT = parseInt(root.dataset.discountPct || '30', 10);
  const DISCOUNT_CODE = root.dataset.discountCode || 'MYRUME';
  const THEME_ROLE = root.dataset.themeRole || 'main';
  const IS_DRAFT = THEME_ROLE === 'unpublished' || THEME_ROLE === 'development';

  // Klaviyo lead capture — public company id + destination list, read from the
  // liquid root. Capture only fires when PREVIEW_MODE is false (see subscribeToKlaviyo).
  const KLAVIYO_COMPANY = root.dataset.klaviyoCompany || '';
  const KLAVIYO_LIST = root.dataset.klaviyoList || '';
  const KLAVIYO_REVISION = '2025-07-15';

  // TEMPORARY (Cooper, 2026-07-03): show the popup on EVERY visit while we edit the
  // capture sequencing, ignoring a saved 'lead'/'declined' state. Set back to false
  // to restore normal behavior (popup only for visitors with no saved state).
  const ALWAYS_SHOW_POPUP = false;

  const STORAGE_KEYS = {
    state: 'rume_state',
    ts: 'rume_state_ts',
    phone: 'rume_phone',
    email: 'rume_email',
    preference: 'rume_preference'
  };

  // Cookie mirror for the state machine. Some viewing surfaces (in-app browsers,
  // private windows, the theme-customizer preview iframe) drop localStorage /
  // sessionStorage between page loads, which made the popup re-open on every
  // navigation. First-party cookies survive there, so state is written to both
  // and read from whichever answers.
  const COOKIE_TTL_S = 60 * 60 * 24 * 365;
  const DECLINE_TTL_S = 60 * 60 * 24; // declined = per-visit; re-eligible after a day
  function getCookie(name) {
    try {
      const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
      return m ? decodeURIComponent(m[1]) : null;
    } catch (e) { return null; }
  }
  function setCookie(name, value, maxAgeSec) {
    try {
      document.cookie = name + '=' + encodeURIComponent(value) + ';path=/' +
        (maxAgeSec != null ? ';max-age=' + maxAgeSec : '') + ';SameSite=Lax;Secure';
    } catch (e) { /* no-op */ }
  }
  function clearCookie(name) {
    try { document.cookie = name + '=;path=/;max-age=0;SameSite=Lax;Secure'; } catch (e) { /* no-op */ }
  }

  const TRIGGER = {
    delayMs: window.matchMedia('(min-width: 768px)').matches ? 2800 : 1500,
    scrollPct: 30,
    tickerStartDelayMs: 600,
    tickerDurationMs: 1500
  };

  const DRAG = {
    closeThresholdPx: 120
  };

  // Copy shown in the retry popup (declined user clicked the corner tab to reopen).
  // Different from first-touch: no count-up animation, copy acknowledges they were
  // about to checkout — the brand going out of its way to make the gift land.
  const RETRY_COPY = {
    eyebrow: 'Before you go',
    body: 'We noticed you were heading to the cart at full price. We’d rather you have the 30 % off — it’s still yours.'
  };

  // ============== ELEMENT REFS ==============
  const popupEl = root.querySelector('[data-rume-popup]');
  const cardEl = root.querySelector('[data-rume-card]');
  const handleEl = root.querySelector('[data-rume-handle]');
  const closeBtn = root.querySelector('[data-rume-close]');
  const declineBtn = root.querySelector('[data-rume-decline]');
  const overlayEl = root.querySelector('[data-rume-overlay]');
  const formEl = root.querySelector('[data-rume-form]');
  const errorEl = root.querySelector('[data-rume-error]');
  const tickerEl = root.querySelector('[data-rume-ticker]');
  const page1El = root.querySelector('[data-rume-page="1"]');
  const phonePageEl = root.querySelector('[data-rume-page="phone"]');
  const phoneFormEl = root.querySelector('[data-rume-phone-form]');
  const phoneErrorEl = root.querySelector('[data-rume-phone-error]');
  const page2El = root.querySelector('[data-rume-page="2"]');
  const pillEls = Array.from(root.querySelectorAll('[data-rume-pref]'));
  const skipBtn = root.querySelector('[data-rume-skip]');
  const cornerEl = root.querySelector('[data-rume-corner]');
  const devPanelEl = root.querySelector('[data-rume-devpanel]');
  const devStateLabelEl = root.querySelector('[data-rume-state-label]');
  const backPageEl = root.querySelector('[data-rume-page="back"]');
  const backLoginBtn = root.querySelector('[data-rume-back-login]');
  const backContinueBtn = root.querySelector('[data-rume-back-continue]');
  // Pricing-engine worker hosts the returning-customer gate check. Email that
  // tripped the gate is stashed so SECURE LOGIN can prefill Shopify's sign-in.
  const WORKER_BASE = 'https://rume-pricing-engine.rume-makerume.workers.dev';
  let gatedPhone = null;
  let gatedEmail = null;

  // Snapshot first-touch copy from rendered HTML so retry mode can restore it on close.
  const eyebrowEl = root.querySelector('.rume-eyebrow');
  const copyEl = root.querySelector('.rume-copy');
  const FIRST_TOUCH = {
    eyebrow: eyebrowEl ? eyebrowEl.innerHTML : '',
    body: copyEl ? copyEl.innerHTML : ''
  };

  // ============== STORAGE ==============
  function readState() {
    let s = { state: null, ts: null, phone: null, email: null, preference: null };
    try {
      s = {
        state: localStorage.getItem(STORAGE_KEYS.state) || null,
        ts: localStorage.getItem(STORAGE_KEYS.ts) || null,
        phone: localStorage.getItem(STORAGE_KEYS.phone) || null,
        email: localStorage.getItem(STORAGE_KEYS.email) || null,
        preference: localStorage.getItem(STORAGE_KEYS.preference) || null
      };
    } catch (e) { /* fall through to cookie mirror */ }
    if (!s.state) s.state = getCookie(STORAGE_KEYS.state);
    if (!s.email) s.email = getCookie(STORAGE_KEYS.email);
    if (s.state === 'declined') {
      // decline is per-visit: expire after DECLINE_TTL_S so returning visitors see the popup again
      const age = s.ts ? (Date.now() - Date.parse(s.ts)) : NaN;
      if (isNaN(age) || age > DECLINE_TTL_S * 1000) {
        // cookie-only reads carry no ts; the declined cookie self-expires via its own max-age
        if (s.ts) { s.state = getCookie(STORAGE_KEYS.state) || null; s.ts = null; }
      }
    }
    return s;
  }
  function setState(state, extras = {}) {
    try {
      localStorage.setItem(STORAGE_KEYS.state, state);
      localStorage.setItem(STORAGE_KEYS.ts, new Date().toISOString());
      if (extras.phone) localStorage.setItem(STORAGE_KEYS.phone, extras.phone);
      if (extras.email) localStorage.setItem(STORAGE_KEYS.email, extras.email);
      if (extras.preference) localStorage.setItem(STORAGE_KEYS.preference, extras.preference);
    } catch (e) { /* no-op */ }
    setCookie(STORAGE_KEYS.state, state, state === 'declined' ? DECLINE_TTL_S : COOKIE_TTL_S);
    if (extras.email) setCookie(STORAGE_KEYS.email, extras.email, COOKIE_TTL_S);
    applyState();
  }
  function clearState() {
    try {
      Object.values(STORAGE_KEYS).forEach(k => localStorage.removeItem(k));
    } catch (e) { /* no-op */ }
    clearCookie(STORAGE_KEYS.state);
    clearCookie(STORAGE_KEYS.email);
    clearCookie(SESSION_SHOWN_KEY);
    applyState();
  }

  // ============== STATE → DOM ==============
  function applyState() {
    const s = readState();
    document.body.dataset.rumeState = s.state || 'anon';
    if (s.preference) document.body.dataset.rumePreference = s.preference;
    else delete document.body.dataset.rumePreference;
    if (cornerEl) {
      if (s.state === 'declined') cornerEl.removeAttribute('hidden');
      else cornerEl.setAttribute('hidden', '');
    }
    if (devStateLabelEl) {
      const label = s.state || 'anon';
      const pref = s.preference ? ` · ${s.preference}` : '';
      devStateLabelEl.textContent = label + pref;
    }
  }

  // ============== POPUP OPEN / CLOSE ==============
  let popupOpenedThisPageLoad = false;

  function openPopup({ mode = 'first' } = {}) {
    if (!popupEl) return;
    // REC3 (welcome-back) arrivals are dropped straight into the custom lead state
    // (30% welcome + first-cup) by the landing page, so the capture popup would only
    // interrupt them. The REC3 hero sets html[data-rume-rec3]; never open here.
    if (document.documentElement.hasAttribute('data-rume-rec3')) return;

    // Swap copy for retry mode vs first-touch
    if (eyebrowEl) eyebrowEl.innerHTML = mode === 'retry' ? RETRY_COPY.eyebrow : FIRST_TOUCH.eyebrow;
    if (copyEl) copyEl.innerHTML = mode === 'retry' ? RETRY_COPY.body : FIRST_TOUCH.body;
    popupEl.dataset.rumeMode = mode;

    popupEl.removeAttribute('hidden');
    popupOpenedThisPageLoad = true;
    document.body.style.overflow = 'hidden';
    if (page1El) page1El.removeAttribute('hidden');
    if (phonePageEl) phonePageEl.setAttribute('hidden', '');
    if (page2El) page2El.setAttribute('hidden', '');
    if (backPageEl) backPageEl.setAttribute('hidden', '');

    if (tickerEl) {
      if (mode === 'retry') {
        // They've seen the count-up — skip the theater, just show the number
        tickerEl.textContent = String(DISCOUNT_PCT);
      } else {
        tickerEl.textContent = '0';
        requestAnimationFrame(() => {
          setTimeout(
            () => animateTicker(0, DISCOUNT_PCT, TRIGGER.tickerDurationMs),
            TRIGGER.tickerStartDelayMs
          );
        });
      }
    }

    setTimeout(() => {
      const emailInput = root.querySelector('#rume-email');
      if (emailInput) emailInput.focus({ preventScroll: true });
    }, 1000);
  }

  function closePopup({ markDeclined = false } = {}) {
    if (!popupEl) return;
    // Restore first-touch copy in case we opened in retry mode
    if (popupEl.dataset.rumeMode === 'retry') {
      if (eyebrowEl) eyebrowEl.innerHTML = FIRST_TOUCH.eyebrow;
      if (copyEl) copyEl.innerHTML = FIRST_TOUCH.body;
    }
    delete popupEl.dataset.rumeMode;
    popupEl.setAttribute('hidden', '');
    document.body.style.overflow = '';
    if (cardEl) {
      cardEl.style.transform = '';
      cardEl.removeAttribute('data-rw-dragging');
    }
    if (markDeclined) {
      const current = readState();
      if (current.state !== 'lead') setState('declined');
    }

    // On the home page only: after the user opts in (state is now 'lead'),
    // scroll them back to the top so they re-experience the page with the
    // banner active and the discount "in the room." On other pages (PDP) we
    // leave them where they were — they're mid-bundle and shouldn't lose context.
    if (!markDeclined && readState().state === 'lead') {
      const path = window.location.pathname;
      const isHome = path === '/' || path === '' || path === '/index.html';
      if (isHome) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }

  // ============== TICKER ANIMATION ==============
  function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }
  function animateTicker(from, to, durationMs) {
    if (!tickerEl) return;
    const start = performance.now();
    function step(now) {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = easeOutCubic(t);
      const value = Math.round(from + (to - from) * eased);
      tickerEl.textContent = String(value);
      if (t < 1) requestAnimationFrame(step);
      else tickerEl.textContent = String(to);
    }
    requestAnimationFrame(step);
  }

  // ============== FORM SUBMIT ==============
  // US phone: accept 10 digits, or 11 with a leading 1. Ignore formatting chars.
  function isValidPhone(s) {
    if (!s || typeof s !== 'string') return false;
    const digits = s.replace(/\D/g, '');
    return digits.length === 10 || (digits.length === 11 && digits[0] === '1');
  }
  function isValidEmail(s) {
    if (!s || typeof s !== 'string') return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
  }

  // US phone → E.164 for Klaviyo (10 digits, or 11 with leading 1).
  function toE164(raw) {
    const d = (raw || '').replace(/\D/g, '');
    if (d.length === 10) return '+1' + d;
    if (d.length === 11 && d[0] === '1') return '+' + d;
    return null;
  }

  // Fire-and-forget lead capture to Klaviyo's public client-subscriptions
  // endpoint (public company id ONLY — never a private key). No-ops unless
  // capture is switched on (PREVIEW_MODE false) and config is present. Never
  // blocks or breaks the popup UX; failures are swallowed.
  function subscribeToKlaviyo(opts) {
    if (PREVIEW_MODE) return;
    if (!KLAVIYO_COMPANY || !KLAVIYO_LIST) return;
    const attrs = { properties: { source_funnel: 'welcome_popup' } };
    const subs = {};
    if (opts.email) {
      attrs.email = opts.email;
      subs.email = { marketing: { consent: 'SUBSCRIBED' } };
    }
    if (opts.phone) {
      const e164 = toE164(opts.phone);
      if (e164) {
        attrs.phone_number = e164;
        subs.sms = { marketing: { consent: 'SUBSCRIBED' }, transactional: { consent: 'SUBSCRIBED' } };
      }
    }
    if (opts.preference) attrs.properties.rume_preference = opts.preference;
    if (Object.keys(subs).length) attrs.subscriptions = subs;
    const body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Welcome Popup',
          profile: { data: { type: 'profile', attributes: attrs } }
        },
        relationships: { list: { data: { type: 'list', id: KLAVIYO_LIST } } }
      }
    };
    try {
      fetch('https://a.klaviyo.com/client/subscriptions?company_id=' + encodeURIComponent(KLAVIYO_COMPANY), {
        method: 'POST',
        headers: { 'Content-Type': 'application/vnd.api+json', revision: KLAVIYO_REVISION },
        body: JSON.stringify(body),
        keepalive: true
      }).catch(function () {});
    } catch (e) { /* no-op */ }
    // Fire a per-submit custom event so recovery flows can trigger on every
    // popup submission (unlike the subscribe metric, which only fires the
    // first time a profile opts in — leaving returning lead-state visitors
    // unable to re-enter recovery). Fire-and-forget alongside the subscribe.
    if (opts.skipEvent) return;
    try {
      const eventProfileAttrs = {};
      if (opts.email) eventProfileAttrs.email = opts.email;
      if (opts.phone) {
        const eventE164 = toE164(opts.phone);
        if (eventE164) eventProfileAttrs.phone_number = eventE164;
      }
      const eventBody = {
        data: {
          type: 'event',
          attributes: {
            properties: { source_funnel: 'welcome_popup' },
            metric: { data: { type: 'metric', attributes: { name: 'Requested Welcome Discount' } } },
            profile: { data: { type: 'profile', attributes: eventProfileAttrs } }
          }
        }
      };
      fetch('https://a.klaviyo.com/client/events?company_id=' + encodeURIComponent(KLAVIYO_COMPANY), {
        method: 'POST',
        headers: { 'Content-Type': 'application/vnd.api+json', revision: KLAVIYO_REVISION },
        body: JSON.stringify(eventBody),
        keepalive: true
      }).catch(function () {});
    } catch (e) { /* no-op */ }
  }

  // Funnel telemetry keyed to the step-1 email: phone-screen abandonment =
  // "Reached Phone Screen" minus "Completed Phone Screen" in Klaviyo metrics.
  function trackFunnelEvent(name) {
    if (PREVIEW_MODE || !KLAVIYO_COMPANY) return;
    var email = readState().email;
    if (!email) return;
    try {
      fetch('https://a.klaviyo.com/client/events?company_id=' + encodeURIComponent(KLAVIYO_COMPANY), {
        method: 'POST',
        headers: { 'Content-Type': 'application/vnd.api+json', revision: KLAVIYO_REVISION },
        body: JSON.stringify({
          data: {
            type: 'event',
            attributes: {
              properties: { source_funnel: 'welcome_popup' },
              metric: { data: { type: 'metric', attributes: { name: name } } },
              profile: { data: { type: 'profile', attributes: { email: email } } }
            }
          }
        }),
        keepalive: true
      }).catch(function () {});
    } catch (e) { /* no-op */ }
  }

  // ============== RETURNING-CUSTOMER GATE ==============
  // Ask the worker whether this phone belongs to a prior customer. Returns true
  // ONLY on an explicit block; ANY uncertainty (network, bad JSON, worker down)
  // returns false so we fail OPEN and never wrongly stop a genuine new customer.
  function checkPhoneBlocked(phone) {
    return fetch(WORKER_BASE + '/welcome/check-phone', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone: phone })
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) { return !!(j && j.ok === true && j.blocked === true); })
      .catch(function () { return false; });
  }

  // Email sibling of the phone gate — the welcome capture is email-first, so this
  // is the PRIMARY returning-customer check. Same fail-open contract.
  function checkEmailBlocked(email) {
    return fetch(WORKER_BASE + '/welcome/check-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email })
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) { return !!(j && j.ok === true && j.blocked === true); })
      .catch(function () { return false; });
  }

  // Cookie set on the order-confirmation page (rume_purchased_at = ISO date).
  // Suppresses the auto-opening welcome popup for ~14 days after a purchase.
  function recentlyPurchased() {
    try {
      var m = document.cookie.match(/(?:^|;\s*)rume_purchased_at=([^;]+)/);
      if (!m) return false;
      var when = new Date(decodeURIComponent(m[1])).getTime();
      if (!when || isNaN(when)) return false;
      var days = (Date.now() - when) / 86400000;
      return days >= 0 && days < 14;
    } catch (e) { return false; }
  }

  // Show the "Welcome Back!" gate page inside the popup. Never grants lead state
  // and never applies a discount — that's the whole point.
  function showWelcomeBack(opts) {
    opts = opts || {};
    if (opts.email) gatedEmail = opts.email;
    if (opts.phone) gatedPhone = opts.phone;
    // A confirmed returning customer must never keep a discount they unlocked on
    // an earlier visit. Any non-'lead' state drops prices back to full — the
    // buybox + offer cards observe body[data-rume-state] and recompute instantly.
    // 'returning' (vs clearing) means no decline-corner nudge and no auto-reopen.
    var extras = {};
    if (opts.email) extras.email = opts.email;
    if (opts.phone) extras.phone = opts.phone;
    setState('returning', extras);
    if (popupEl && popupEl.hasAttribute('hidden')) {
      popupEl.removeAttribute('hidden');
      document.body.style.overflow = 'hidden';
      popupOpenedThisPageLoad = true;
    }
    if (page1El) page1El.setAttribute('hidden', '');
    if (phonePageEl) phonePageEl.setAttribute('hidden', '');
    if (page2El) page2El.setAttribute('hidden', '');
    if (backLoginBtn) backLoginBtn.removeAttribute('disabled');
    if (backPageEl) backPageEl.removeAttribute('hidden');
  }

  // Email is the primary capture — it unlocks lead state and advances to the
  // optional-phone beat (phone is deliberately a second step so it doesn't
  // suppress sign-ups from people who won't hand over a number up front).
  if (formEl) {
    formEl.addEventListener('submit', function (e) {
      e.preventDefault();
      const input = formEl.querySelector('#rume-email');
      const email = input ? input.value.trim() : '';
      if (!isValidEmail(email)) {
        if (errorEl) errorEl.removeAttribute('hidden');
        if (input) input.setAttribute('aria-invalid', 'true');
        return;
      }
      if (errorEl) errorEl.setAttribute('hidden', '');
      if (input) input.removeAttribute('aria-invalid');

      // Email-keyed returning-customer gate: a confirmed prior redeemer is bounced
      // to the Welcome Back page instead of unlocking the discount again.
      var submitBtn = formEl.querySelector('[data-rume-submit]');
      if (submitBtn) submitBtn.setAttribute('disabled', '');
      checkEmailBlocked(email).then(function (blocked) {
        if (submitBtn) submitBtn.removeAttribute('disabled');
        if (blocked) {
          showWelcomeBack({ email: email });
          return;
        }
        setState('lead', { email: email });
        subscribeToKlaviyo({ email: email });
        advanceToPhone();
      });
    });
  }

  // Perceived-mandatory phone — no skip affordance; the X-close is the only
  // exit, which is exactly the abandonment we measure (Reached vs Completed
  // Phone Screen events). Empty or malformed input shows the error. A valid
  // number runs the returning-customer gate before it's kept (a repeat
  // customer who gives a known number is bounced to the Welcome Back page).
  if (phoneFormEl) {
    phoneFormEl.addEventListener('submit', function (e) {
      e.preventDefault();
      const input = phoneFormEl.querySelector('#rume-phone');
      const phone = input ? input.value.trim() : '';
      if (!phone || !isValidPhone(phone)) {
        if (phoneErrorEl) phoneErrorEl.removeAttribute('hidden');
        if (input) input.setAttribute('aria-invalid', 'true');
        return;
      }
      if (phoneErrorEl) phoneErrorEl.setAttribute('hidden', '');
      if (input) input.removeAttribute('aria-invalid');

      var submitBtn = phoneFormEl.querySelector('[data-rume-phone-submit]');
      if (submitBtn) submitBtn.setAttribute('disabled', '');
      checkPhoneBlocked(phone).then(function (blocked) {
        if (submitBtn) submitBtn.removeAttribute('disabled');
        if (blocked) {
          showWelcomeBack({ phone: phone });
          return;
        }
        setState('lead', { phone: phone });
        // Include the step-1 email so Klaviyo attaches the phone + SMS consent to
        // the SAME profile instead of minting an email-less orphan (which leaves
        // every flow's phone-is-set split false and kills the SMS leg). skipEvent:
        // the recovery trigger already fired on the email submit.
        subscribeToKlaviyo({ email: readState().email || undefined, phone: phone, skipEvent: true });
        trackFunnelEvent('Completed Phone Screen');
        closePopup({ markDeclined: false });
      });
    });
  }

  // ============== WELCOME BACK GATE BUTTONS ==============
  // Neither grants lead state. SECURE LOGIN sends them to Shopify's hosted
  // passwordless sign-in (email -> one-time code; Shopify owns the email).
  if (backLoginBtn) {
    backLoginBtn.addEventListener('click', function () {
      backLoginBtn.setAttribute('disabled', '');
      var loginUrl = '/customer_authentication/login';
      if (gatedEmail) loginUrl += '?login_hint=' + encodeURIComponent(gatedEmail);
      window.location.href = loginUrl;
    });
  }
  if (backContinueBtn) {
    backContinueBtn.addEventListener('click', function () { closePopup({ markDeclined: false }); });
  }

  function advanceToPhone() {
    if (!page1El || !phonePageEl) return;
    trackFunnelEvent('Reached Phone Screen');
    page1El.setAttribute('hidden', '');
    phonePageEl.removeAttribute('hidden');
    setTimeout(() => {
      const pi = root.querySelector('#rume-phone');
      if (pi) pi.focus({ preventScroll: true });
    }, 400);
  }

  // ============== PILL SELECT ==============
  pillEls.forEach((pill) => {
    pill.addEventListener('click', function () {
      const pref = pill.dataset.rumePref;
      pillEls.forEach((p) => p.setAttribute('aria-checked', p === pill ? 'true' : 'false'));
      setState('lead', { preference: pref });
      setTimeout(() => closePopup({ markDeclined: false }), 320);
    });
  });

  if (skipBtn) {
    skipBtn.addEventListener('click', function () {
      closePopup({ markDeclined: false });
    });
  }

  // ============== CLOSE INTERACTIONS ==============
  if (closeBtn) closeBtn.addEventListener('click', function () { closePopup({ markDeclined: true }); });
  if (declineBtn) declineBtn.addEventListener('click', function () { closePopup({ markDeclined: true }); });
  if (overlayEl) overlayEl.addEventListener('click', function () { closePopup({ markDeclined: true }); });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && popupEl && !popupEl.hasAttribute('hidden')) {
      closePopup({ markDeclined: true });
    }
  });

  // Corner is only visible in declined state — always opens in retry mode
  if (cornerEl) {
    cornerEl.addEventListener('click', function () { openPopup({ mode: 'retry' }); });
  }

  // ============== DRAG-TO-DISMISS (mobile sheet) ==============
  function setupDrag() {
    if (!handleEl || !cardEl) return;
    let startY = null;
    let currentY = null;
    let isDragging = false;
    function isMobile() { return window.matchMedia('(max-width: 991px)').matches; }
    function onStart(e) {
      if (!isMobile()) return;
      const touch = e.touches ? e.touches[0] : e;
      startY = touch.clientY;
      currentY = startY;
      isDragging = true;
      cardEl.dataset.rwDragging = 'true';
    }
    function onMove(e) {
      if (!isDragging) return;
      const touch = e.touches ? e.touches[0] : e;
      currentY = touch.clientY;
      const delta = Math.max(0, currentY - startY);
      cardEl.style.transform = `translateY(${delta}px)`;
      if (overlayEl) {
        const fadePct = Math.min(1, delta / 300);
        overlayEl.style.opacity = String(1 - fadePct * 0.6);
      }
    }
    function onEnd() {
      if (!isDragging) return;
      isDragging = false;
      const delta = currentY - startY;
      cardEl.removeAttribute('data-rw-dragging');
      if (delta >= DRAG.closeThresholdPx) {
        cardEl.style.transition = 'transform 240ms ease-out';
        cardEl.style.transform = 'translateY(100%)';
        setTimeout(() => {
          cardEl.style.transition = '';
          cardEl.style.transform = '';
          if (overlayEl) overlayEl.style.opacity = '';
          closePopup({ markDeclined: true });
        }, 240);
      } else {
        cardEl.style.transition = 'transform 240ms ease-out';
        cardEl.style.transform = '';
        if (overlayEl) overlayEl.style.transition = 'opacity 240ms ease-out';
        if (overlayEl) overlayEl.style.opacity = '';
        setTimeout(() => {
          cardEl.style.transition = '';
          if (overlayEl) overlayEl.style.transition = '';
        }, 260);
      }
      startY = null;
      currentY = null;
    }
    handleEl.addEventListener('touchstart', onStart, { passive: true });
    handleEl.addEventListener('touchmove', onMove, { passive: true });
    handleEl.addEventListener('touchend', onEnd);
    handleEl.addEventListener('touchcancel', onEnd);
    handleEl.addEventListener('mousedown', (e) => { onStart(e); document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', endMouse); });
    function endMouse() { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', endMouse); onEnd(); }
  }

  // ============== AUTO-OPEN TRIGGERS ==============
  let scrollListenerAttached = false;
  // Per-tab-session 'already shown' marker: once the popup auto-opens this
  // session, don't auto-open again on subsequent page navigations — even if the
  // visitor never submitted or dismissed. Survives page switches (sessionStorage),
  // clears when the tab/session ends. Manual re-open (declined corner button) is
  // unaffected because it doesn't route through maybeAutoOpen.
  const SESSION_SHOWN_KEY = 'rume_popup_shown';
  function shownThisSession() {
    let shown = false;
    try { shown = !!sessionStorage.getItem(SESSION_SHOWN_KEY); } catch (e) { /* no-op */ }
    // Session-cookie mirror (no max-age = dies with the browser session) covers
    // surfaces where sessionStorage doesn't survive page navigations.
    return shown || getCookie(SESSION_SHOWN_KEY) === '1';
  }
  function markShownThisSession() {
    try { sessionStorage.setItem(SESSION_SHOWN_KEY, '1'); } catch (e) { /* no-op */ }
    setCookie(SESSION_SHOWN_KEY, '1', null);
  }
  function maybeAutoOpen() {
    if (recentlyPurchased()) return;
    if (!ALWAYS_SHOW_POPUP && readState().state) return;
    if (!ALWAYS_SHOW_POPUP && shownThisSession()) return;
    if (popupOpenedThisPageLoad) return;
    markShownThisSession();
    openPopup();
  }
  function setupTriggers() {
    if (!ALWAYS_SHOW_POPUP && readState().state) return;
    setTimeout(maybeAutoOpen, TRIGGER.delayMs);
    function onScroll() {
      const docH = document.documentElement.scrollHeight - window.innerHeight;
      if (docH <= 0) return;
      const pct = (window.scrollY / docH) * 100;
      if (pct >= TRIGGER.scrollPct) {
        maybeAutoOpen();
        if (scrollListenerAttached) {
          window.removeEventListener('scroll', onScroll, { passive: true });
          scrollListenerAttached = false;
        }
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    scrollListenerAttached = true;
  }

  // ============== REFRESH-ON-HOME RESET ==============
  function maybeResetOnHome() {
    if (!IS_DRAFT) return;
    const path = window.location.pathname;
    const isHome = path === '/' || path === '' || path === '/index.html';
    if (!isHome) return;
    let navType = 'navigate';
    try {
      const navEntry = performance.getEntriesByType('navigation')[0];
      if (navEntry && navEntry.type) navType = navEntry.type;
    } catch (e) { /* ignore */ }
    const referrer = document.referrer || '';
    let referrerOrigin = '';
    try { referrerOrigin = referrer ? new URL(referrer).origin : ''; } catch (e) { referrerOrigin = ''; }
    const isInternalReferrer = referrerOrigin && referrerOrigin === window.location.origin;
    const isReload = navType === 'reload';
    const isFreshNav = navType === 'navigate' && !isInternalReferrer;
    if (isReload || isFreshNav) {
      clearState();
    }
  }

  // ============== PREVIEW SHORTCUTS ==============
  function applyPreviewShortcuts() {
    if (!IS_DRAFT) return;
    const params = new URLSearchParams(window.location.search);
    const preview = params.get('preview');
    if (!preview) return;
    if (preview === 'reset') clearState();
    else if (preview === 'welcome') setState('lead', { email: 'preview@rume.test' });
    else if (preview === 'skip') setState('declined');
    params.delete('preview');
    const newQs = params.toString();
    const newUrl = window.location.pathname + (newQs ? '?' + newQs : '') + window.location.hash;
    history.replaceState(null, '', newUrl);
  }

  // ============== DEV PANEL ==============
  function setupDevPanel() {
    if (!devPanelEl) return;
    devPanelEl.querySelectorAll('button[data-rume-dev]').forEach((btn) => {
      btn.addEventListener('click', function () {
        const action = btn.dataset.rumeDev;
        if (action === 'reset') clearState();
        else if (action === 'welcome') setState('lead', { email: 'preview@rume.test' });
        else if (action === 'skip') setState('declined');
        else if (action === 'show') openPopup();
      });
    });
  }

  // ============== INLINE OFFER-CARD EMAIL CAPTURE (order page / future LPs) ==============
  // The order-page section file sits at Shopify's 256KB template limit, so its
  // welcome-gift offer card ships without inline capture markup. Any .rp-shop-offer
  // card that lacks a capture node gets the email sign-up window injected + wired
  // here, mirroring the home page's inline close-offer card (which carries its own
  // markup/script and is skipped because its capture node already exists).
  function setupInlineOfferCapture() {
    const wraps = [];
    document.querySelectorAll('.rp-shop-offer').forEach(function (wrap) {
      if (wrap.querySelector('.rp-shop-offer__capture')) return;
      const card = wrap.querySelector('.rp-shop-offer__card');
      if (!card) return;
      wrap.setAttribute('data-rume-cap-injected', '');
      const cap = document.createElement('div');
      cap.className = 'rp-shop-offer__capture';
      cap.innerHTML =
        '<label class="rp-close-cap__label" for="rp-order-email">Drop your email and we’ll apply your 30% welcome to this order.</label>' +
        '<div class="rp-close-cap__row">' +
          '<input class="rp-close-cap__input" id="rp-order-email" type="email" name="email" inputmode="email" autocomplete="email" placeholder="you@example.com" data-rume-close-email>' +
          '<button class="rp-close-cap__btn" type="button" data-rume-close-claim>Claim Gift</button>' +
        '</div>' +
        '<p class="rp-close-cap__err" data-rume-close-err hidden>Please enter a valid email address.</p>' +
        '<p class="rp-close-cap__consent">Your email stays with us. Never sold, never shared.</p>';
      card.appendChild(cap);
      wraps.push(wrap);
    });
    if (!wraps.length) return;

    function syncAll() {
      const st = readState().state === 'lead' ? 'claimed' : 'unclaimed';
      wraps.forEach(function (w) { w.setAttribute('data-state', st); });
    }
    syncAll();
    try {
      new MutationObserver(syncAll).observe(document.body, {
        attributes: true, attributeFilter: ['data-rume-state']
      });
    } catch (e) { /* non-fatal */ }

    wraps.forEach(function (el) {
      const input = el.querySelector('[data-rume-close-email]');
      const btn = el.querySelector('[data-rume-close-claim]');
      const err = el.querySelector('[data-rume-close-err]');

      function claim() {
        const email = (input && input.value || '').trim();
        if (!isValidEmail(email)) {
          if (err) err.hidden = false;
          if (input) input.focus();
          return;
        }
        if (err) err.hidden = true;
        // Enforcement gate: a returning customer must never unlock discount
        // prices. Fails OPEN (grants lead) if the gate is unavailable.
        if (btn) btn.disabled = true;
        checkEmailBlocked(email).then(function (blocked) {
          if (btn) btn.disabled = false;
          if (blocked) { showWelcomeBack({ email: email }); return; }
          // Same capture path as the popup — lands in the welcome flow.
          try { subscribeToKlaviyo({ email: email }); } catch (e) { /* no-op */ }
          // setState flips body[data-rume-state]; every price observer on the
          // page (buybox, banner, this card) recomputes from that.
          setState('lead', { email: email });
          syncAll();
        });
      }

      if (btn) btn.addEventListener('click', claim);
      if (input) input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); claim(); }
      });
    });
  }

  // ============== BOOT ==============
  function boot() {
    maybeResetOnHome();
    applyPreviewShortcuts();
    applyState();
    setupDevPanel();
    setupDrag();
    setupTriggers();
    setupInlineOfferCapture();
  }

  // Expose the gate so the home-page inline claim() card shares one enforcement
  // path + the same Welcome Back popup as the fullscreen welcome funnel.
  window.RumeWelcome = {
    checkPhoneBlocked: checkPhoneBlocked,
    checkEmailBlocked: checkEmailBlocked,
    showWelcomeBack: showWelcomeBack,
    subscribeToKlaviyo: subscribeToKlaviyo,
    // Inline claim cards (home + order page) flip the lead state through here so
    // the cookie mirror stays in sync with localStorage.
    setLeadState: function (extras) { setState('lead', extras || {}); }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();


